import { Box, Flex, Radio, RadioGroup, Text, VStack } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import DatePicker from "react-datepicker";

import { useRequestParams } from "contexts/RequestParamsProvider";
import dayjs from "dayjs";
import { formatDate } from "util/statHelpers";
import { localToUTCDate, utcToLocalDate } from "util/time";
import { DocumentsFilter, FilterActionButtons } from "../DocumentsFilter";
import {
  RADIO_BUTTON_PAST_OPTIONS,
  oldestDocumentDate,
} from "../filterConstants";
import { GenericFilterProps } from "./generic-filter-props";

type RadioButtonListFilterProps = {
  filterOptions?: Array<{
    label: string;
    value: string;
  }>;
  defaultFilterType?: string;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
} & GenericFilterProps;

const RadioButtonDatePickerAndActionButtons = ({
  filterKey,
  filterOptions,
  selectedFilterType,
  startDateFilter,
  endDateFilter,
  defaultFilterType,
  defaultStartDate,
  defaultEndDate,
}: {
  filterKey: string;
  filterOptions: Array<{
    label: string;
    value: string;
  }>;
  selectedFilterType?: string;
  startDateFilter?: Date;
  endDateFilter?: Date;
  defaultFilterType?: string;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
}) => {
  const { onChangeDateRange, onChangeString } = useRequestParams();
  const [filterType, setFilterType] = useState(selectedFilterType);
  const [customStartDate, setCustomStartDate] = useState(startDateFilter);
  const [customEndDate, setCustomEndDate] = useState(endDateFilter);
  const onChange = ([start, end]: [Date | null, Date | null]) => {
    setCustomStartDate(localToUTCDate(start ?? undefined));
    setCustomEndDate(localToUTCDate(end ?? undefined));
  };

  const baseDate = dayjs().startOf("day");
  const yearsOfTracking =
    oldestDocumentDate.getFullYear() - new Date().getFullYear();

  const startDate = useMemo(() => {
    switch (filterType) {
      case "past7Days":
        return baseDate.add(-7, "day").toDate();
      case "past30Days":
        return baseDate.add(-1, "month").toDate();
      case "past6Months":
        return baseDate.add(-6, "month").toDate();
      case "pastYear":
        return baseDate.add(-1, "year").toDate();
      case "pastDue":
        return baseDate.add(yearsOfTracking, "year").toDate();
      case "next7Days":
      case "next30Days":
      case "next6Months":
      case "nextYear":
        return baseDate.toDate();
      default:
        return undefined;
    }
  }, [baseDate, filterType, yearsOfTracking]);

  const endDate = useMemo(() => {
    switch (filterType) {
      case "past7Days":
      case "past30Days":
      case "past6Months":
      case "pastYear":
        return baseDate.toDate();
      case "pastDue":
        return baseDate.add(-1, "day").toDate();
      case "next7Days":
        return baseDate.add(7, "day").toDate();
      case "next30Days":
        return baseDate.add(1, "month").toDate();
      case "next6Months":
        return baseDate.add(6, "month").toDate();
      case "nextYear":
        return baseDate.add(1, "year").toDate();
      default:
        return undefined;
    }
  }, [baseDate, filterType]);

  const onSet = () => {
    onChangeString(`${filterKey}Type`, filterType);

    filterType === "customRange"
      ? onChangeDateRange(filterKey, customStartDate, customEndDate)
      : onChangeDateRange(filterKey, startDate, endDate);
  };

  const onReset = () => {
    onChangeString(`${filterKey}Type`, defaultFilterType);
    setCustomStartDate(undefined);
    setCustomEndDate(undefined);
    onChangeDateRange(filterKey, defaultStartDate, defaultEndDate);
  };

  const isSetDisabled = useMemo(() => {
    return (
      !filterType ||
      (filterType === "customRange" &&
        customStartDate === startDateFilter &&
        customEndDate === endDateFilter) ||
      (filterType !== "customRange" && filterType === selectedFilterType)
    );
  }, [
    filterType,
    customStartDate,
    customEndDate,
    startDateFilter,
    endDateFilter,
    selectedFilterType,
  ]);

  return (
    <>
      <VStack mb={6} gap={0} alignItems="flex-start" maxW="272px">
        {filterOptions.map((filterOption) => (
          <RadioGroup key={filterOption.value}>
            <Radio
              isChecked={filterType === filterOption.value}
              onChange={() => setFilterType(filterOption.value)}
              px={6}
              py={1}
              w="full"
              _hover={{ bg: "navy.50" }}
            >
              <Box as="span" fontSize="sm">
                {filterOption.label}
              </Box>
            </Radio>
          </RadioGroup>
        ))}
      </VStack>
      {filterType === "customRange" && (
        <>
          <Flex px={6} justifyContent="center">
            <DatePicker
              onChange={onChange}
              selected={utcToLocalDate(customStartDate)}
              startDate={utcToLocalDate(customStartDate)}
              endDate={utcToLocalDate(customEndDate)}
              minDate={
                filterKey === "dueDate" ? baseDate.toDate() : oldestDocumentDate
              }
              maxDate={filterKey === "dueDate" ? null : baseDate.toDate()}
              selectsRange
              inline
            />
          </Flex>
          <Text textAlign="center" textStyle="body2" px={16} py={2}>
            {customStartDate && formatDate(customStartDate)} -{" "}
            {customEndDate && formatDate(customEndDate)}
          </Text>
        </>
      )}

      <FilterActionButtons
        onReset={onReset}
        isResetDisabled={!startDate && !customStartDate && !customEndDate}
        onSet={onSet}
        isSetDisabled={isSetDisabled}
      />
    </>
  );
};

export const RadioButtonDateFilter = ({
  filterKey,
  variant,
  name,
  filterOptions = RADIO_BUTTON_PAST_OPTIONS,
  ...rest
}: RadioButtonListFilterProps) => {
  const { filters } = useRequestParams();
  const startDateFilter = filters[`${filterKey}StartDate`] as Date | undefined;
  const endDateFilter = filters[`${filterKey}EndDate`] as Date | undefined;
  const selectedFilterType = filters[`${filterKey}Type`] as string | undefined;

  return (
    <DocumentsFilter
      name={name}
      isSet={!!startDateFilter || !!endDateFilter}
      variant={variant}
    >
      <RadioButtonDatePickerAndActionButtons
        startDateFilter={startDateFilter}
        endDateFilter={endDateFilter}
        filterKey={filterKey}
        selectedFilterType={selectedFilterType}
        filterOptions={filterOptions}
        {...rest}
      />
    </DocumentsFilter>
  );
};
